// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import isNumber from 'lodash/isNumber';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {avatarsStore} from 'common/report/stores/avatars';
import {getEntityCreatorDisplayName, isSystemUser, userDetailsStore} from 'common/report/stores/user-details';
import {getProjectId} from 'common/report/util';
import {Tooltip} from 'common/components/tooltip';
import {ICON_PROPERTIES_BY_SIZE, IconSizeType} from 'common/constants';
import {isStaffEmail} from 'common/report/util';
import {openTeammateSidebar} from 'common/widgets/teammate-sidebar/util';
import {Placement} from 'common/components/tooltip/types';
import {panel2react} from 'common/util/react';
import {useStoreAndFetchOnce} from 'common/util/component-helpers';
import {IronContextRegistry} from 'common/report/context-registry';
import {CurrentProjectResource} from 'common/report/model-resources/projects';

import {template} from './template';
import css from './index.inline.styl';
import mpAvatarUrl from './mp-avatar.svg';
import {AvatarType, track, TrackingEvent, TrackingEventProperty, UserIconTrackProperties} from './tracking';

type UserIconState = {
  avatar: string;
  isAvatarImageLoaded: boolean;
};

export type UserIconAttrs = {
  'img-src'?: string;
  name?: string;
  id?: number;
  'disable-tooltip'?: boolean;
  'disable-sidebar'?: boolean;
  size?: IconSizeType;
  'creator-date'?: string;
  'tooltip-placement'?: Placement;
  'is-offline'?: boolean;
  email?: string;
  'include-label'?: boolean;
  'label-prefix'?: string;
  'label-suffix'?: string;
  'subscribe-to-updates'?: boolean;
  'track-properties'?: UserIconTrackProperties;
  'staff-member-avatar-uuid'?: string;
};

type JsxProps = {attrs: UserIconAttrs};
const COLORS_TOTAL = 10;
export class UserIcon extends Component<UserIconState, UserIconAttrs, any, any, IronContextRegistry> {
  private declare avatarStoreCallback: () => void;
  private declare userDetailsStoreCallback: () => void;

  static get attrsSchema(): AttrsSchema<UserIconAttrs> {
    return {
      ...super.attrsSchema,
      'img-src': {type: `string`, description: `link to the image`},
      name: `string`,
      id: {
        type: `number`,
        default: null,
        description: `ID of the user displayed by the icon.
        Used to get the avatar uuid of the user as long as they are in the current organization.`,
      },
      'disable-tooltip': `boolean`,
      'disable-sidebar': {
        type: `boolean`,
        description: `used to determine whether or not to allow the sidebar to open on click`,
      },
      size: {type: `string`, enum: Object.values(IconSizeType)},
      // used for realtime enabled canvas. show dimmed avatar if the user is not online
      'is-offline': `boolean`,
      'tooltip-placement': Tooltip.attrsSchema[`placement`],
      email: {type: `string`, description: `used to determine if staff user`},
      'include-label': {type: `boolean`, description: `show user name for the icon to the right`},
      'label-prefix': {type: `string`},
      'label-suffix': {type: `string`},
      'subscribe-to-updates': {type: `boolean`, description: `whether to subscribe to avatar store updates`},
      'track-properties': {default: {}, type: `json`, tsType: `UserIconTrackProperties`},
      'staff-member-avatar-uuid': {
        type: `string`,
        description: `Staff member avatar UUID used for displaying real staff members with their pictures.
        For example, displaying the organization's account CSM. This skips the need to have a staff member's user ID exposed.`,
      },
    };
  }
  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `id` || attr === `size`) {
      this.updateAvatarUrlFromStore();
    }
  }
  resourceCustomSubscriptions = new Map([
    [
      CurrentProjectResource,
      () => {
        this.fetchAvatars();
      },
    ],
  ]);

  connectedCallback() {
    super.connectedCallback();
    this.avatarStoreCallback = () => {
      this.updateAvatarUrlFromStore();
      !this.attr(`subscribe-to-updates`) && avatarsStore.unsubscribe(this.avatarStoreCallback);
    };

    this.userDetailsStoreCallback = () => {
      this.fetchAvatars();
      this.update();
      userDetailsStore.unsubscribe(this.userDetailsStoreCallback);
    };

    avatarsStore.subscribe(this.avatarStoreCallback);
    if (!this.attr(`img-src`)) {
      useStoreAndFetchOnce(this, userDetailsStore, this.userDetailsStoreCallback);
    }
    this.fetchAvatars();
  }

  disconnectedCallback() {
    avatarsStore.unsubscribe(this.avatarStoreCallback);
    super.disconnectedCallback();
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        avatar: null,
        isAvatarImageLoaded: false,
      },
      contexts: [`currentProject` as const],
      helpers: {
        getBackgroundColorClass: () => {
          const colorId = isNumber(this.attr(`id`)) ? this.attr(`id`) % COLORS_TOTAL : `default`;
          return `color-${colorId}`;
        },
        getIconProperties: () => {
          return ICON_PROPERTIES_BY_SIZE[this.attr(`size`)];
        },
        getAvatarUrl: () => {
          if (this.attr(`img-src`)) {
            return this.attr(`img-src`);
          } else if (this.shouldShowStaffAvatar()) {
            return mpAvatarUrl;
          }
          return this.state.avatar;
        },
        handleIconClick: (ev) => {
          let avatarType = AvatarType.Empty;
          if (this.helpers.getAvatarUrl()) {
            avatarType = AvatarType.Photo;
          } else if (this.attr(`name`)) {
            avatarType = AvatarType.Name;
          }
          if (this.helpers.shouldOpenSidebar()) {
            // Stop propagation as we don't want clicking on this in say a table row to
            // count as clicking on the row
            ev.stopPropagation();
            ev.preventDefault();
            openTeammateSidebar({
              email: this.attr(`email`),
              name: this.attr(`name`),
              id: this.attr(`id`),
              'project-id': parseInt(getProjectId()),
            });
          }
          track(TrackingEvent.IconClick, {
            [TrackingEventProperty.AvatarType]: avatarType,
            ...this.attr(`track-properties`),
          });
        },
        handleAvatarImageLoaded: () => {
          this.update({isAvatarImageLoaded: true});
        },
        getDisplayName: () => {
          return getEntityCreatorDisplayName(this.attr(`email`), this.attr(`name`));
        },
        shouldOpenSidebar: () =>
          this.attr(`email`) &&
          !this.attr(`disable-sidebar`) &&
          !this.shouldShowStaffAvatar() &&
          !userDetailsStore.value.isGuest,
      },
    };
  }

  updateAvatarUrlFromStore() {
    const uuid = this.attr(`staff-member-avatar-uuid`) || avatarsStore.value?.avatars?.[this.attr(`id`)];
    const size = this.helpers.getIconProperties()?.imgSize;

    const avatar = uuid && size ? `${avatarsStore.value.base}/${uuid}/${size}x${size}.png` : null;
    this.update({avatar});
  }

  fetchAvatars() {
    // if img-src is passed in then we don't need to do the implicit avatar fetch
    if (this.attr(`img-src`)) return;
    const organizationId = this.getContext(`currentProject`).get().value?.organizationId;

    // do not attempt to fetch avatars from an org the user doesn't belong to
    if (userDetailsStore.value?.organizations[organizationId]) {
      organizationId && avatarsStore.fetch({organizationId});
    }
  }

  shouldShowStaffAvatar() {
    return (
      (!this.attr(`staff-member-avatar-uuid`) && isStaffEmail(this.attr(`email`)) && !userDetailsStore.value.isStaff) ||
      isSystemUser({email: this.attr(`email`)})
    );
  }
}

export const UserIconEl = defineAndMakeJsxFactory<JsxProps>(`mp-user-icon`, UserIcon);
export const UserIconReact = panel2react<JsxProps>(`mp-user-icon`, UserIcon);
